// Team picker grid: grouped by group A-L
// Mobile: 2-col grid. Tablet+: 4-col grid. Desktop: 4-col with group labels
import CountryFlag from "@/components/CountryFlag";
import { useTranslation } from "react-i18next";

type Team = {
  id: number;
  name: string;
  code: string;
  flag: string;
  group: string;
  confederation: string;
};

type Props = {
  teams: Team[];
  selectedTeams: Team[];
  onToggle: (team: Team) => void;
  searchQuery: string;
};

const GROUPS = ["A","B","C","D","E","F","G","H","I","J","K","L"];

export default function TeamGrid({ teams, selectedTeams, onToggle, searchQuery }: Props) {
  const { t } = useTranslation();
  const selectedIds = new Set(selectedTeams.map((tm) => tm.id));
  const isFiltered = searchQuery.trim().length > 0;

  if (isFiltered) {
    // Flat grid when searching
    if (teams.length === 0) {
      return (
        <div className="flex flex-col items-center py-16 text-muted-foreground" data-testid="no-results">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mb-3 opacity-40">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <p className="text-sm">{t("teams.no_results", { query: searchQuery })}</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 stagger-children" data-testid="team-grid-filtered">
        {teams.map((team) => (
          <TeamChip
            key={team.id}
            team={team}
            selected={selectedIds.has(team.id)}
            onToggle={onToggle}
          />
        ))}
      </div>
    );
  }

  // Grouped display
  return (
    <div className="space-y-6" data-testid="team-grid-grouped">
      {GROUPS.map((group) => {
        const groupTeams = teams.filter((tm) => tm.group === group);
        if (groupTeams.length === 0) return null;
        return (
          <div key={group}>
            <div className="flex items-center gap-3 mb-3">
              <span
                className="text-xs font-bold uppercase tracking-widest text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-md"
              >
                {t("teams.group_label", { group })}
              </span>
              <div className="flex-1 h-px bg-border/40" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {groupTeams.map((team) => (
                <TeamChip
                  key={team.id}
                  team={team}
                  selected={selectedIds.has(team.id)}
                  onToggle={onToggle}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamChip({ team, selected, onToggle }: { team: Team; selected: boolean; onToggle: (t: Team) => void }) {
  return (
    <button
      onClick={() => onToggle(team)}
      className={`
        relative flex items-center gap-2.5 px-3 py-3 rounded-lg border text-start
        transition-all duration-150 cursor-pointer group w-full
        hover-elevate
        ${selected
          ? "team-chip-selected border-primary/70 bg-primary/10"
          : "border-border/50 bg-card/60 hover:border-border text-foreground"
        }
      `}
      aria-pressed={selected}
      data-testid={`team-chip-${team.id}`}
    >
      {/* Flag */}
      <CountryFlag code={team.code} emoji={team.flag} size={22} />

      {/* Name + code */}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-tight truncate">{team.name}</div>
        <div className="text-xs text-muted-foreground leading-none mt-0.5">{team.code}</div>
      </div>

      {/* Selected checkmark */}
      {selected && (
        <div className="shrink-0 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="hsl(43 95% 55%)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
      )}
    </button>
  );
}
